import type { RouteObject } from 'react-router';
import { page } from '@/lib/lazy-route';
import { settingsRoutes as platformSettings } from '@/modules/platform/routes';
import { settingsRoutes as extensionSettings } from '@/modules/extension/routes';
import { settingsRoutes as integrationSettings } from '@/modules/integrations/routes';
import { settingsRoutes as backupSettings } from '@/modules/backups/routes';
import { settingsRoutes as aiSettings } from '@/modules/ai/routes';
import { settingsRoutes as monitoringSettings } from '@/modules/monitoring/routes'; // Phase M (m-core)
import { settingsRoutes as bankingSettings } from '@/modules/banking/routes'; // G3/G4 (g-banking)
import { settingsRoutes as supportAccessSettings } from '@/modules/platform-console/routes'; // Phase I (I-D)
import { settingsRoutes as pensionSettings } from '@/modules/payroll/pensions/routes'; // Phase H (H-E pensions)
import { settingsRoutes as subscriptionSettings } from '@/modules/subscription/routes'; // Phase I (I-B, I2)
import { settingsRoutes as platformStatusSettings } from '@/modules/platform-status/routes'; // I5 (trust track)

const pages = () => import('./admin-pages');
const security = () => import('./security-pages');
const identity = () => import('@/modules/security/identity-pages');
// A7/A8: developer settings live in their own module, framed by the admin layout.
const developer = () => import('../developer/developer-pages');

export const routes: RouteObject[] = [
  {
    path: 'admin',
    lazy: page(pages, (m) => m.AdminLayout),
    children: [
      { index: true, lazy: page(pages, (m) => m.UsersPage) },
      { path: 'roles', lazy: page(pages, (m) => m.RolesPage) },
      { path: 'access', lazy: page(security, (m) => m.SecurityPage) }, // A4/A11: groups, org access, encryption
      { path: 'audit', lazy: page(pages, (m) => m.AuditPage) },
      // Phase I (I-C): branding v2 and custom domains
      { path: 'branding', lazy: page(() => import('../branding/branding-page'), (m) => m.BrandingPage) },
      { path: 'domains', lazy: page(() => import('../branding/domains-page'), (m) => m.DomainsPage) },
      { path: 'email', lazy: page(pages, (m) => m.EmailSettingsPage) },
      // Phase A infra: System (jobs) and Notifications (defaults, templates, delivery log)
      ...platformSettings,
      // A2/A3 (config track)
      { path: 'custom-fields', lazy: page(() => import('../config/custom-fields-page'), (m) => m.CustomFieldsPage) },
      { path: 'lists', lazy: page(() => import('../config/lists-page'), (m) => m.ListsPage) },
      { path: 'api-keys', lazy: page(developer, (m) => m.ApiKeysPage) },
      { path: 'api-reference', lazy: page(developer, (m) => m.ApiReferencePage) },
      { path: 'webhooks', lazy: page(developer, (m) => m.WebhooksPage) },
      // Phase A identity (A5/A6): security policy, SSO and sessions.
      { path: 'security', lazy: page(() => import('@/modules/security/security-page'), (m) => m.SecurityPage) },
      // Phase K (K2, K4): SAML, SCIM provisioning and OIDC clients.
      { path: 'saml', lazy: page(identity, (m) => m.SamlPage) },
      { path: 'provisioning', lazy: page(identity, (m) => m.ProvisioningPage) },
      { path: 'oidc-clients', lazy: page(identity, (m) => m.OidcClientsPage) },
      ...extensionSettings, // B5: browser extension policy
      // Phase B (B9/B10): Import (IT Glue) and Microsoft 365.
      ...integrationSettings,
      ...backupSettings, // Track 5: Data export (F7's Backups page moved to the platform console)
      ...aiSettings, // Phase F (F5): AI assistant
      ...monitoringSettings, // Phase M (m-core): Monitoring probes
      ...bankingSettings, // G3/G4 (g-banking): Open Banking
      ...supportAccessSettings, // Phase I (I-D): Settings ▸ Support access
      ...pensionSettings, // Phase H (H-E pensions): Payroll > Pensions
      ...subscriptionSettings, // Phase I (I-B, I2): Subscription; Plans for platform admins
      ...platformStatusSettings, // I5 (trust track): Denwen MSP's own status page, platform admins only
    ],
  },
];
export const orgRoutes: RouteObject[] = [];
